/**
 * Variant C document catalog — the regulatory taxonomy that drives
 * the LoomFresh idle screen.
 *
 * Port of doc-catalog.js from the design handoff, with strong types.
 *
 * Design principle (per the Build Spec): "The catalog is data."
 * Adding a new doc type is a one-place edit — it shouldn't require
 * any component change. The shape below is the contract any
 * downstream renderer (LoomFresh, future PhaseN renderers) must
 * accept.
 *
 * Source-of-truth question (Question 5 from the Phase-0 plan): this
 * file is hand-curated for now. The doctype tree (doctypes/<ID>/manifest.yaml)
 * is the runtime authority for the backend; this catalog is the writer-facing
 * taxonomy. Server-derivation is a Phase-3 follow-up — the data shapes
 * are deliberately compatible (code, name, framework, auth) so a
 * generated catalog can drop in.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The high-level domain a writer is operating in. Drives the
 * top-level tab in LoomFresh.
 *   - pharma: FDA / EMA / ICH outputs (CSR, IND, NDA/BLA, PSUR, ...)
 *   - device: MDR / FDA device pathways (CER, CIP, 510(k), PMA, ...)
 *   - shared: cross-domain artefacts (IB, protocol synopsis)
 */
export type DocDomain = "pharma" | "device" | "shared";

/**
 * Regulatory body / framework tag rendered as a small chip next to
 * the doc code. Free-form string — additional bodies can be added
 * without touching the type. Common values today:
 *   FDA · EMA · ICH · MDR · ISO · "FDA · EMA"
 */
export type RegulatoryAuth = string;

export interface DocCatalogEntry {
  /** Short code used as the primary identifier and display label
   *  (e.g. "CSR", "PSUR", "NDA/BLA"). */
  code: string;
  /** Long human-readable name. */
  name: string;
  /** Governing standard / regulation (e.g. "ICH E3", "MDR Annex XIV").
   *  Optional — shared/utility entries may not have one. */
  framework?: string;
  /** Regulatory body tag — shown as a chip next to the code. */
  auth?: RegulatoryAuth;
  /** When true, this entry is a parent and ``children`` lists its
   *  sub-documents (e.g. CTD M1-M5 under NDA/BLA). The renderer
   *  shows it as expandable. */
  expandable?: boolean;
  /** Sub-documents — only populated when ``expandable`` is true. */
  children?: DocCatalogEntry[];
  /** Optional one-line note shown beneath the name (e.g.
   *  "2.5 Clinical Overview · 2.7 Clinical Summary" for M2). */
  note?: string;
  /**
   * Canonical backend doc_type value (e.g. "CSR", "PSUR_PHARMA").
   * Matches what `list_doc_types()` returns from
   * ``doctypes/<ID>/manifest.yaml``'s ``document_type:`` field — the
   * existing repo-wide convention the classic UI also uses. The tree's
   * directory name IS the code, so casing here is the consumer-visible
   * identity and the directory name at once.
   *
   * When undefined, the backend has no config yet and Start Draft
   * must be disabled — the writer-facing display ``code`` doesn't
   * always match this canonical value (e.g. display "PSUR/PBRER"
   * → "PSUR_PHARMA"; display "PSUR" device-side → "PSUR_DEVICE";
   * the slash + device/pharma split is not a clean string mapping).
   *
   * Lives in the catalog (data) so adding a backend config = one
   * data edit; adding an undefined entry shows up as "coming soon"
   * in the picker without any UI change.
   */
  backendDocType?: string;
}

export interface DocCatalogPhase {
  /** Lifecycle phase label — groups related docs in the picker
   *  (e.g. "IND & pre-clinical", "Marketing application"). */
  phase: string;
  docs: DocCatalogEntry[];
}

export interface SourceCatalogEntry {
  /** Short code. */
  code: string;
  /** Long name. */
  name: string;
}

/**
 * Per-output-doctype source vocabulary. Mapping is keyed by the
 * output doc's ``code`` field (e.g. "CSR", "CER"). Each value is the
 * ordered list of source-doc types that output typically draws from.
 *
 * v1 SHAPE NOTES (worth a regulatory SME pass before any production cut):
 *   - The lists are best-effort first drafts compiled from ICH /
 *     MDR / FDA framework docs + the design handoff. They are NOT
 *     gospel — different sponsors use different source vocabularies,
 *     and adaptive trial designs change the mix.
 *   - The freetext escape hatch in the LibraryApprovalModal lets a
 *     writer tag a doc with a vocabulary the catalog doesn't cover
 *     (e.g. "TLF — AE listing", "Patient narrative bundle"). When a
 *     custom value gets used repeatedly, that's a catalog gap to
 *     promote into this map.
 *   - Multiplicity / "one big TLF doc vs many tables" is Pattern 2
 *     in Spec 12 — deferred to v1.1 there. The source vocabulary
 *     here uses "TLF" as a single conceptual entry; the resolver
 *     handles whether the library has it as one file or many.
 *
 * Fallback: when an output doc isn't in this map (or its sources
 * list is empty), LoomFresh falls back to ``defaultSources`` — the
 * old union shape so the framing band always shows something.
 */
export type SourceCatalog = Record<string, SourceCatalogEntry[]>;

/**
 * The catalog is laid out as three domain branches plus a per-doctype
 * sources map. Sources are read-only inputs — never appear in the
 * "what can I draft" picker.
 */
export interface DocCatalog {
  pharma: DocCatalogPhase[];
  device: DocCatalogPhase[];
  shared: DocCatalogPhase[];
  /** Per-output-doctype source vocabularies. Asthra reads + cites
   *  these; never edits them. */
  sources: SourceCatalog;
  /** Catch-all source list shown when the picked output doc isn't
   *  in the map (or its entry is empty). */
  defaultSources: SourceCatalogEntry[];
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export const DOC_CATALOG: DocCatalog = {
  pharma: [
    {
      phase: "IND & pre-clinical",
      docs: [
        {
          code: "IND",
          name: "Investigational New Drug application",
          framework: "21 CFR 312",
          auth: "FDA",
        },
        {
          code: "CMC",
          name: "CMC Module 3 — Small Molecule (NDA)",
          framework: "ICH M4Q",
          auth: "ICH",
          backendDocType: "CMC_SMALL_MOLECULE",
        },
        {
          // The ANDA counterpart of the NDA Module 3 above. Two of its eCTD
          // leaves (3.2.P.1, 3.2.P.3.3) used to sit below it as standalone
          // rows of their own; those doctypes were removed on 2026-09-15 —
          // the parent declares them as leaf documents of one Module 3, so
          // this single row is the whole ANDA family again.
          code: "CMC-ANDA",
          name: "CMC Module 3 — Small Molecule (ANDA, US Generic)",
          framework: "ICH M4Q",
          auth: "ICH",
          backendDocType: "CMC_ANDA_SMALL_MOLECULE",
        },
        {
          // The biotech Module 3: a monoclonal antibody or recombinant
          // protein, biosimilar or not. Its own doctype rather than a variant
          // of the two above, because the cell-bank, viral-safety and
          // comparability obligations have no small-molecule counterpart.
          code: "CMC-BIO",
          name: "CMC Module 3 — Biologic (mAb / recombinant protein)",
          framework: "ICH M4Q",
          auth: "ICH",
          backendDocType: "CMC_BIOLOGIC",
        },
        {
          code: "IB",
          name: "Investigator's Brochure",
          framework: "ICH E6",
          auth: "ICH",
        },
        {
          code: "Protocol",
          name: "Clinical Trial Protocol (Drug)",
          framework: "ICH M11 / E6(R3)",
          auth: "ICH",
          backendDocType: "PROTOCOL_DRUG",
        },
        {
          // Bioequivalence protocol for a generic (ANDA / abridged) product:
          // PROTOCOL_DRUG's M11 skeleton with an ICH M13A interior.
          code: "Protocol-BE",
          name: "Bioequivalence Trial Protocol (Generic)",
          framework: "ICH M13A / M11",
          auth: "ICH",
          backendDocType: "PROTOCOL_BE",
        },
        {
          code: "CSR-BE",
          name: "Bioavailability / Bioequivalence Study Report",
          framework: "ICH E3 / M13A / M10",
          auth: "ICH",
          backendDocType: "CSR_BE",
        },
        {
          // Comparative clinical pharmacology (PK similarity) protocol for a
          // proposed biosimilar, in the M11 skeleton.
          code: "Protocol-BIOSIM",
          name: "Biosimilar PK Similarity Trial Protocol",
          framework: "ICH M11 · FDA / EMA biosimilar guidance",
          auth: "FDA · EMA",
          backendDocType: "PROTOCOL_BIOSIM",
        },
        {
          code: "IMPD-Q",
          name: "IMPD — Quality (Investigational Medicinal Product Dossier)",
          framework: "EU CTR Annex I G / EMA QWP & BWP IMP guidelines",
          auth: "EMA",
          backendDocType: "IMPD_QUALITY",
        },
        {
          code: "IMPD-S/E",
          name: "IMPD — Safety and Efficacy (non-clinical and clinical part)",
          framework: "EU CTR Annex I G",
          auth: "EU",
          backendDocType: "IMPD_SAFETY_EFFICACY",
        },
        {
          code: "CTA cover letter",
          name: "Clinical Trial Application Cover Letter (EU CTR / UK combined review)",
          framework: "EU CTR Annex I B",
          auth: "EU · MHRA",
          backendDocType: "CTA_COVER_LETTER",
        },
        {
          code: "IND cover letter",
          name: "IND Cover Letter (initial IND)",
          framework: "21 CFR 312.23(a)(1)",
          auth: "FDA",
          backendDocType: "IND_COVER_LETTER",
        },
        {
          code: "IND plan",
          name: "IND Introductory Statement and General Investigational Plan",
          framework: "21 CFR 312.23(a)(3)",
          auth: "FDA",
          backendDocType: "IND_GENERAL_PLAN",
        },
        {
          code: "CMC-IND",
          name: "IND CMC Information (Phase 1)",
          framework: "21 CFR 312.23(a)(7)",
          auth: "FDA",
          backendDocType: "CMC_IND_PHASE1",
        },
        {
          code: "M2-IND",
          name: "IND Pharmacology and Toxicology Summaries (Module 2.4 / 2.6)",
          framework: "21 CFR 312.23(a)(8) · ICH M4S",
          auth: "FDA",
          backendDocType: "CTD_M2_IND",
        },
      ],
    },
    {
      // Sponsor↔FDA meeting requests. These span the IND-to-marketing
      // lifecycle rather than a single phase, so they get their own group
      // instead of crowding "IND & pre-clinical". Each backendDocType
      // matches a ``document_type:`` in doctypes/BRIEFING_*/manifest.yaml,
      // so the rows are draftable, not "coming soon".
      phase: "FDA meetings & briefing packages",
      docs: [
        {
          code: "Pre-IND",
          name: "FDA Type B (Pre-IND) Meeting Briefing Package",
          framework: "FDA Formal Meetings",
          auth: "FDA",
          backendDocType: "BRIEFING_PRE_IND",
        },
        {
          code: "Type A",
          name: "FDA Type A Meeting Briefing Package",
          framework: "FDA Formal Meetings",
          auth: "FDA",
          backendDocType: "BRIEFING_TYPE_A",
        },
        {
          code: "Type B (EOP)",
          name: "FDA Type B End-of-Phase Meeting Briefing Package",
          framework: "FDA Formal Meetings",
          auth: "FDA",
          backendDocType: "BRIEFING_TYPE_B_EOP",
        },
        {
          code: "Type C",
          name: "FDA Type C Meeting Briefing Package",
          framework: "FDA Formal Meetings",
          auth: "FDA",
          backendDocType: "BRIEFING_TYPE_C",
        },
        {
          code: "Type D",
          name: "FDA Type D Meeting Briefing Package",
          framework: "FDA Formal Meetings",
          auth: "FDA",
          backendDocType: "BRIEFING_TYPE_D",
        },
        {
          code: "INTERACT",
          name: "FDA INTERACT Meeting Briefing Package",
          framework: "CBER-OTP INTERACT",
          auth: "FDA",
          backendDocType: "BRIEFING_INTERACT",
        },
        {
          code: "AdComm",
          name: "FDA Advisory Committee Briefing Document",
          framework: "FDA AdComm Guidance",
          auth: "FDA",
          backendDocType: "BRIEFING_ADCOM",
        },
        {
          code: "Orphan",
          name: "Orphan Drug Designation Request",
          framework: "21 CFR 316 / EU Reg 141/2000",
          auth: "FDA · EMA",
          backendDocType: "ORPHAN_DESIGNATION",
        },
        {
          code: "Meeting request",
          name: "Meeting / Scientific Advice Request (any agency)",
          framework: "FDA Formal Meetings / EMA/4260/2001",
          auth: "FDA · EMA",
          backendDocType: "MEETING_REQUEST",
        },
        {
          code: "SA Briefing",
          name: "Scientific Advice Briefing Document (EMA / MHRA / national agencies)",
          framework: "EMA Scientific Advice / Protocol Assistance",
          auth: "EMA",
          backendDocType: "BRIEFING_SCIENTIFIC_ADVICE",
        },
      ],
    },
    {
      phase: "Clinical (trial-phase)",
      docs: [
        {
          code: "CSR",
          name: "Clinical Study Report",
          framework: "ICH E3",
          auth: "ICH",
          backendDocType: "CSR",
        },
        {
          // Same ICH E3 report as CSR with §11 collapsed from 28
          // modules to 10 (client Ubuntu requirement, 2026-08-17).
          // Sits directly under CSR because it is an alternative to
          // it, not a separate kind of document.
          code: "CSR-Abbr",
          name: "Abbreviated CSR (Ubuntu)",
          framework: "ICH E3",
          auth: "ICH",
          backendDocType: "CSR_ABBREVIATED_UBUNTU",
        },
        {
          code: "DSUR",
          name: "Development Safety Update Report",
          framework: "ICH E2F",
          auth: "ICH",
          backendDocType: "DSUR",
        },
        {
          code: "Lay summary",
          name: "Summary of Clinical Trial Results for Laypersons",
          framework: "EU CTR Annex V",
          auth: "EU",
          backendDocType: "LAY_SUMMARY",
        },
        {
          code: "SAP",
          name: "Statistical Analysis Plan",
          framework: "ICH E9 / E9(R1)",
          auth: "ICH",
          backendDocType: "SAP",
        },
        {
          code: "iPSP",
          name: "Initial Pediatric Study Plan (FDA iPSP)",
          framework: "FDA iPSP guidance (July 2020) / FD&C Act 505B",
          auth: "FDA",
          backendDocType: "PEDIATRIC_PLAN",
        },
        {
          code: "PIP",
          name: "Paediatric Investigation Plan (EU PIP / product-specific waiver)",
          framework: "EMA PIP template v4 / Regulation (EC) 1901/2006",
          auth: "EMA · MHRA",
          backendDocType: "PIP_EU",
        },
      ],
    },
    {
      phase: "Marketing application",
      docs: [
        {
          // Top-level, selectable CTD Module 2 entry. The M2 child under
          // NDA/BLA below stays as taxonomy context, but the picker only
          // renders top-level rows (children are deferred — see
          // DoctypePicker's DECISION note), so M2 lives here to be
          // draftable today. backendDocType matches
          // doctypes/CTD_M2/manifest.yaml's ``document_type: CTD_M2``.
          code: "CTD M2",
          name: "Common Technical Document — Module 2 summaries",
          framework: "ICH M4",
          auth: "ICH",
          note: "2.5 Clinical Overview · 2.7 Clinical Summary",
          backendDocType: "CTD_M2",
        },
        {
          // The drafted parts of CTD Module 5: the tabular listing of all
          // clinical studies, the integrated analyses at 5.3.5.3, reports of
          // post-marketing experience and the literature references. The
          // constituent study reports themselves are filed, not drafted here —
          // a CSR is the CSR doctype's job.
          code: "CTD-M5",
          name: "Common Technical Document — Module 5 drafted parts",
          framework: "ICH M4E",
          auth: "ICH",
          note: "5.2 Tabular listing · 5.3.5.3 Integrated analyses · 5.4 References",
          backendDocType: "CTD_M5",
        },
        {
          code: "NDA/BLA",
          name: "New Drug / Biologics License Application",
          framework: "CTD (ICH M4)",
          auth: "FDA · EMA",
          expandable: true,
          children: [
            {
              code: "M1",
              name: "Regional administrative info",
              framework: "CTD",
              auth: "FDA · EMA",
            },
            {
              code: "M2",
              name: "Common Technical Document summaries",
              framework: "CTD M2",
              auth: "ICH",
              note: "2.5 Clinical Overview · 2.7 Clinical Summary",
            },
            {
              code: "M3",
              name: "Quality (CMC)",
              framework: "CTD M3",
              auth: "ICH",
            },
            {
              code: "M4",
              name: "Nonclinical study reports",
              framework: "CTD M4",
              auth: "ICH",
            },
            {
              code: "M5",
              name: "Clinical study reports",
              framework: "CTD M5",
              auth: "ICH",
              note: "contains CSRs",
            },
          ],
        },
        {
          code: "Cover letter",
          name: "Regulatory Submission Cover Letter (any agency)",
          framework: "FDA eCTD TCG §3.1.2 / EU CTR Annex II B",
          auth: "FDA · EMA",
          backendDocType: "REG_COVER_LETTER",
        },
        {
          code: "HA Response",
          name: "Response to Health Authority Questions (IR / CRL / LoQ / RFI)",
          framework: "FDA 21 CFR 314.110 / EMA LoQ-LoOI template",
          auth: "FDA · EMA",
          backendDocType: "HA_QUERY_RESPONSE",
        },
        {
          code: "USPI",
          name: "US Prescribing Information (PLR) with Patient Labeling",
          framework: "21 CFR 201.56-201.57",
          auth: "FDA",
          backendDocType: "LABELING_USPI",
        },
        {
          code: "EU PI",
          name: "EU Product Information (SmPC / Annex II / Labelling / PL)",
          framework: "EMA QRD template v10.4",
          auth: "EMA",
          backendDocType: "LABELING_EU_PI",
        },
      ],
    },
    {
      phase: "Post-market",
      docs: [
        {
          code: "PSUR/PBRER",
          name: "Periodic Safety Update / Periodic Benefit-Risk Evaluation",
          framework: "ICH E2C",
          auth: "ICH · EMA",
          backendDocType: "PSUR_PHARMA",
        },
        {
          code: "RMP",
          name: "Risk Management Plan (EU RMP / national annexes; US REMS)",
          framework: "GVP Module V",
          auth: "EMA",
          backendDocType: "RISK_MANAGEMENT_PLAN",
        },
      ],
    },
  ],

  device: [
    {
      phase: "Design & development",
      docs: [
        {
          code: "DVT Report",
          name: "Design Verification Test Report",
          framework: "FDA 21 CFR 820.30 / ISO 13485:2016",
          auth: "FDA · ISO",
          backendDocType: "DVT_REPORT",
        },
        {
          code: "Q-Sub",
          name: "FDA Pre-Submission (Q-Submission)",
          framework: "FDA Q-Submission Program",
          auth: "FDA",
          backendDocType: "QSUB_DEVICE",
        },
        {
          code: "RMF",
          name: "Risk Management File (plan & report)",
          framework: "ISO 14971:2019",
          auth: "ISO",
          backendDocType: "RISK_MANAGEMENT_FILE_DEVICE",
        },
      ],
    },
    {
      phase: "Clinical evidence",
      docs: [
        {
          code: "CER",
          name: "Clinical Evaluation Report",
          framework: "MDR Annex XIV",
          auth: "MDR",
          backendDocType: "CER",
        },
        {
          code: "CEP",
          name: "Clinical Evaluation Plan",
          framework: "MDR Annex XIV Part A",
          auth: "MDR",
          backendDocType: "CEP_DEVICE",
        },
        {
          code: "CIP",
          name: "Clinical Investigation Plan",
          framework: "ISO 14155",
          auth: "ISO",
        },
      ],
    },
    {
      phase: "Marketing application",
      docs: [
        {
          code: "510(k)",
          name: "Premarket notification",
          framework: "21 CFR 807",
          auth: "FDA",
          backendDocType: "PREMARKET_510K",
        },
        {
          code: "De Novo",
          name: "De Novo classification",
          framework: "21 CFR 860",
          auth: "FDA",
        },
        {
          code: "PMA",
          name: "Premarket approval",
          framework: "21 CFR 814",
          auth: "FDA",
        },
        {
          code: "EU TF",
          name: "EU MDR Technical File",
          framework: "MDR Annex II",
          auth: "MDR",
          backendDocType: "TECH_DOC_MDR",
        },
      ],
    },
    {
      phase: "Post-market",
      docs: [
        {
          code: "PSUR",
          name: "Periodic Safety Update Report",
          framework: "MDR Annex III",
          auth: "MDR",
          backendDocType: "PSUR_DEVICE",
        },
        {
          code: "PMCF Plan",
          name: "Post-Market Clinical Follow-up Plan",
          framework: "MDR Annex XIV Part B / MDCG 2020-7",
          auth: "MDR",
          backendDocType: "PMCF_PLAN",
        },
        {
          code: "PMCF Report",
          name: "Post-Market Clinical Follow-up Evaluation Report",
          framework: "MDR Annex XIV Part B / MDCG 2020-8",
          auth: "MDR",
          backendDocType: "PMCF_EVAL_REPORT",
        },
      ],
    },
  ],

  shared: [
    {
      phase: "Investigator-facing",
      docs: [
        {
          code: "IB",
          name: "Investigator's Brochure",
          framework: "ICH E6",
          auth: "ICH",
        },
        {
          code: "SYN",
          name: "Protocol synopsis",
          framework: "ICH E6",
          auth: "ICH",
        },
      ],
    },
  ],

  // -------------------------------------------------------------------
  // Per-output-doctype source vocabularies. SME-validation pending —
  // see SourceCatalog interface docstring for posture notes.
  // -------------------------------------------------------------------
  sources: {
    // ---- Pharma trial-phase ------------------------------------------------
    CSR: [
      { code: "Protocol", name: "Clinical study protocol" },
      { code: "CRF", name: "Case Report Forms" },
      { code: "ICF", name: "Informed Consent Form" },
      { code: "SAP", name: "Statistical Analysis Plan" },
      { code: "TLF", name: "Tables, Listings, Figures (output of SAP)" },
      { code: "IB", name: "Investigator's Brochure" },
      {
        code: "Prior CSRs",
        name: "Earlier CSRs (adaptive / amendment context)",
      },
      { code: "Lab manuals", name: "Central / local lab manuals" },
    ],
    // Same inputs as the full CSR — the abbreviation is in the output
    // structure (§11 modules collapsed), not in what it is drafted from.
    "CSR-Abbr": [
      { code: "Protocol", name: "Clinical study protocol" },
      { code: "CRF", name: "Case Report Forms" },
      { code: "ICF", name: "Informed Consent Form" },
      { code: "SAP", name: "Statistical Analysis Plan" },
      { code: "TLF", name: "Tables, Listings, Figures (output of SAP)" },
      { code: "IB", name: "Investigator's Brochure" },
      {
        code: "Prior CSRs",
        name: "Earlier CSRs (adaptive / amendment context)",
      },
      { code: "Lab manuals", name: "Central / local lab manuals" },
    ],
    DSUR: [
      { code: "IB", name: "Investigator's Brochure" },
      { code: "Prior DSURs", name: "Prior Development Safety Update Reports" },
      { code: "Line listings", name: "Trial-phase safety line listings" },
      { code: "Protocol", name: "Clinical study protocol(s) in scope" },
    ],

    // ---- Pharma marketing application -------------------------------------
    "NDA/BLA": [
      { code: "Protocol", name: "Clinical study protocol(s)" },
      { code: "CRF", name: "Case Report Forms" },
      { code: "SAP", name: "Statistical Analysis Plan" },
      { code: "TLF", name: "Tables, Listings, Figures" },
      { code: "CSR", name: "Constituent CSRs (Module 5)" },
      { code: "Nonclinical", name: "Nonclinical study reports (Module 4)" },
      { code: "CMC", name: "Quality / CMC docs (Module 3)" },
      {
        code: "Prior NDA/BLA",
        name: "Prior submissions (amendments, supplements)",
      },
    ],
    M1: [
      {
        code: "Regional admin",
        name: "FDA / EMA regional administrative docs",
      },
    ],
    M2: [
      { code: "Module 3 source", name: "CMC summary inputs" },
      { code: "Module 4 source", name: "Nonclinical study reports" },
      { code: "Module 5 source", name: "Constituent CSRs" },
    ],
    // Keyed by the top-level picker code ("CTD M2"); mirrors the M2
    // child's source vocabulary above.
    "CTD M2": [
      { code: "Module 3 source", name: "CMC summary inputs" },
      { code: "Module 4 source", name: "Nonclinical study reports" },
      { code: "Module 5 source", name: "Constituent CSRs" },
    ],
    // Keyed by the top-level picker code ("CTD-M5").
    "CTD-M5": [
      { code: "CSR", name: "Clinical Study Reports and their synopses" },
      { code: "TLF", name: "Tables, Listings, Figures (study and integrated)" },
      { code: "SAP", name: "Statistical Analysis Plans (study and integrated)" },
      { code: "Integrated analysis", name: "Integrated analysis reports and datasets" },
      { code: "Bioanalytical", name: "Bioanalytical and immunogenicity assay reports" },
      { code: "Literature", name: "Cited literature references" },
    ],
    M3: [
      {
        code: "CMC docs",
        name: "Quality / drug substance / drug product docs",
      },
    ],
    M4: [
      {
        code: "Nonclinical reports",
        name: "Nonclinical study reports + appendices",
      },
    ],
    M5: [
      { code: "CSR", name: "Clinical Study Reports" },
      { code: "TLF", name: "Tables, Listings, Figures" },
      { code: "Protocol", name: "Clinical study protocols" },
    ],

    // ---- Pharma post-market -----------------------------------------------
    "PSUR/PBRER": [
      {
        code: "Safety database",
        name: "Sponsor pharmacovigilance database (ICSRs)",
      },
      { code: "Literature", name: "Periodic literature search results" },
      { code: "Prior PSURs", name: "Prior PSUR / PBRER reports" },
      { code: "RSI", name: "Reference Safety Information" },
      { code: "Trial safety", name: "Trial-phase safety reports in period" },
    ],

    // ---- Device clinical evidence -----------------------------------------
    CER: [
      {
        code: "Clinical literature",
        name: "Published clinical literature on the device / equivalents",
      },
      {
        code: "Prior CER",
        name: "Prior Clinical Evaluation Report (for updates)",
      },
      { code: "PMS reports", name: "Post-market surveillance reports" },
      { code: "Complaint logs", name: "Customer complaints + vigilance data" },
      {
        code: "Study reports",
        name: "Sponsor-conducted clinical study reports",
      },
      { code: "IFU", name: "Instructions For Use" },
      { code: "Design docs", name: "Device design + risk management docs" },
      { code: "PSUR data", name: "Most recent PSUR safety data" },
    ],
    CIP: [
      {
        code: "Investigator info",
        name: "Investigator brochure / device dossier",
      },
      { code: "Device description", name: "Device specs + intended use" },
      {
        code: "Prior clinical data",
        name: "Prior clinical data on the device / equivalents",
      },
    ],

    // ---- Device design & development -------------------------------------
    "DVT Report": [
      {
        code: "Test Protocol",
        name: "Approved test protocol (acceptance criteria, methods, sample size)",
      },
      {
        code: "Component datasheets",
        name: "Component / material datasheets (part numbers, dimensional specs)",
      },
      {
        code: "Raw test data",
        name: "Raw bench test data (measured values, equipment IDs, deviations)",
      },
    ],

    // ---- Device marketing application -------------------------------------
    "510(k)": [
      {
        code: "Predicate docs",
        name: "Predicate device 510(k) summaries / labelling",
      },
      {
        code: "Performance tests",
        name: "Bench / animal / clinical performance test reports",
      },
      { code: "Device specs", name: "Device specifications + intended use" },
      { code: "Biocomp", name: "Biocompatibility evaluation" },
    ],
    "De Novo": [
      { code: "Performance tests", name: "Performance test reports" },
      { code: "Risk analysis", name: "Risk analysis + mitigation evidence" },
      { code: "Device specs", name: "Device specifications" },
    ],
    PMA: [
      {
        code: "Clinical study reports",
        name: "Pivotal + supportive clinical study reports",
      },
      { code: "Manufacturing", name: "Manufacturing + quality system info" },
      { code: "Performance tests", name: "Bench + animal performance reports" },
      { code: "Risk analysis", name: "Risk analysis + mitigation evidence" },
    ],
    "EU TF": [
      { code: "Design docs", name: "Design + manufacturing documentation" },
      { code: "Risk management", name: "Risk management file" },
      { code: "CER", name: "Clinical Evaluation Report" },
      { code: "PMCF", name: "Post-Market Clinical Follow-up plan + reports" },
    ],

    // ---- Device post-market -----------------------------------------------
    PSUR: [
      // device PSUR (MDR Annex III) — distinct from pharma PSUR/PBRER
      { code: "PMS reports", name: "Post-market surveillance data" },
      { code: "Vigilance data", name: "Vigilance / serious-incident reports" },
      { code: "Complaint logs", name: "Customer complaints" },
      { code: "PMCF data", name: "Post-Market Clinical Follow-up findings" },
      { code: "Prior PSURs", name: "Prior MDR PSUR reports" },
    ],
    "PMCF Plan": [
      { code: "Prior CER", name: "Prior Clinical Evaluation Report" },
      { code: "PMS reports", name: "Post-market surveillance data" },
      { code: "Literature", name: "Periodic literature surveillance" },
    ],
    "PMCF Report": [
      { code: "Prior CER", name: "Prior Clinical Evaluation Report" },
      { code: "PMS reports", name: "Post-market surveillance data" },
      { code: "Literature", name: "Periodic literature surveillance" },
    ],

    // ---- Pharma IND / pre-clinical ---------------------------------------
    IND: [
      { code: "Protocol", name: "Clinical study protocol" },
      { code: "IB", name: "Investigator's Brochure" },
      { code: "CMC", name: "Chemistry, Manufacturing, Controls" },
      { code: "Nonclinical", name: "Nonclinical study summaries" },
    ],
    CMC: [
      {
        code: "Drug substance",
        name: "Drug substance characterization + specs",
      },
      { code: "Drug product", name: "Drug product manufacturing + specs" },
      { code: "Stability", name: "Stability data" },
    ],
    IB: [
      { code: "Nonclinical", name: "Nonclinical study summaries" },
      { code: "Trial data", name: "Available trial-phase clinical data" },
      { code: "Prior IBs", name: "Prior Investigator Brochures" },
    ],
    Protocol: [
      {
        code: "Prior protocols",
        name: "Prior protocols (templates / amendments)",
      },
      { code: "IB", name: "Investigator's Brochure" },
      { code: "SAP draft", name: "Draft Statistical Analysis Plan" },
    ],

    // ---- Shared / investigator-facing --------------------------------------
    SYN: [{ code: "Protocol", name: "Full clinical study protocol" }],
  },

  // Catch-all when the chosen output doc isn't in the map yet. Same
  // shape as before so the framing band degrades gracefully.
  defaultSources: [
    { code: "Protocol", name: "Clinical study protocol" },
    { code: "CRF", name: "Case Report Forms" },
    { code: "ICF", name: "Informed Consent Form" },
    { code: "SAP", name: "Statistical Analysis Plan" },
    { code: "TLF", name: "Tables, Listings, Figures" },
  ],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Phase list for a domain, or [] if the domain key is unknown. */
export function phasesForDomain(domain: DocDomain): DocCatalogPhase[] {
  return DOC_CATALOG[domain] ?? [];
}

/**
 * A doctype as returned by the backend's `GET /doc-types` — the set that
 * actually has an authoring config today (so it is wired/selectable). Shape is
 * intentionally permissive; only `code` is load-bearing for the merge.
 */
export interface ServerDocType {
  /** Canonical backend doc_type value (e.g. "CSR", "PSUR_PHARMA"). */
  code: string;
  display_name?: string;
  standard?: string;
  domain?: string;
  phase?: string;
  framework?: string;
  auth?: RegulatoryAuth;
  backendDocType?: string;
}

/**
 * The static catalog for a domain with the server's onboarded doctypes spliced
 * in: a static entry whose `code`/`backendDocType` matches a server doctype is
 * marked wired (its `backendDocType` is set), and any server doctype with no
 * static home is appended under its phase. Server-only doctypes are what make
 * a row selectable — without the merge every row shows as "coming soon".
 */
export function mergedPhasesForDomain(domain: DocDomain, serverDocTypes: ServerDocType[] = []): DocCatalogPhase[] {
  const byCode = new Map<string, ServerDocType>();
  for (const s of serverDocTypes) byCode.set(s.code, s);
  const seen = new Set<string>();

  const wire = (d: DocCatalogEntry): DocCatalogEntry => {
    const match = byCode.get(d.code) ?? (d.backendDocType ? byCode.get(d.backendDocType) : undefined);
    const children = d.children?.map(wire);
    if (match) {
      seen.add(match.code);
      return { ...d, backendDocType: d.backendDocType ?? match.backendDocType ?? match.code, children };
    }
    return { ...d, children };
  };

  const merged: DocCatalogPhase[] = phasesForDomain(domain).map((ph) => ({
    phase: ph.phase,
    docs: ph.docs.map(wire),
  }));

  // Server doctypes with no static entry → group them under their own phase.
  const leftovers = serverDocTypes.filter((s) => !seen.has(s.code) && (s.domain == null || s.domain === domain));
  if (leftovers.length > 0) {
    const byPhase = new Map<string, DocCatalogEntry[]>();
    for (const s of leftovers) {
      const phase = s.phase || "Available doctypes";
      const entry: DocCatalogEntry = {
        code: s.code,
        name: s.display_name ?? s.code,
        framework: s.framework ?? s.standard,
        auth: s.auth,
        backendDocType: s.backendDocType ?? s.code,
      };
      const bucket = byPhase.get(phase);
      if (bucket) bucket.push(entry);
      else byPhase.set(phase, [entry]);
    }
    for (const [phase, docs] of byPhase) merged.push({ phase, docs });
  }

  return merged;
}

/**
 * Source vocabulary for one output doc-type. Falls back to
 * defaultSources when the picked code isn't in the map (or its
 * entry is empty) — keeps the framing band non-empty so writers
 * always see *something* about inputs.
 *
 * Pass null/undefined for ``code`` to get defaultSources directly
 * (used when no doc is yet picked).
 */
export function sourcesForDocCode(code: string | null | undefined): SourceCatalogEntry[] {
  if (!code) return DOC_CATALOG.defaultSources;
  const entry = DOC_CATALOG.sources[code];
  if (entry && entry.length > 0) return entry;
  return DOC_CATALOG.defaultSources;
}

/** Reverse lookup: backend doc_type (e.g. "PSUR_PHARMA") → display code
 *  ("PSUR"). Returns null when no catalog entry's backendDocType matches
 *  (unmapped/future doc types) — caller falls back to the raw backend
 *  string rather than fabricating a code. */
// ---------------------------------------------------------------------------
// Private workspace templates
// ---------------------------------------------------------------------------

/** Backend code prefix of a private, workspace-scoped template (server
 *  ``loader.PRIVATE_CODE_PREFIX``). These are never in ``DOC_CATALOG``: they
 *  come from ``GET /doc-types?workspace_id=`` and their UI code IS the backend
 *  code. */
export const PRIVATE_TEMPLATE_PREFIX = "WS_";

export function isPrivateTemplateCode(code: string | null | undefined): boolean {
  return !!code && code.toUpperCase().startsWith(PRIVATE_TEMPLATE_PREFIX);
}

/** Display names of the private templates the server last listed, so a stored
 *  code can be labelled after the picker closes. Module-level on purpose: the
 *  catalog lookups that need it (``lookupDoctypeChoice``) are pure functions
 *  called from many places, and the static catalog is module-level too. */
const privateTemplateNames = new Map<string, string>();

export function rememberPrivateTemplates(entries: ReadonlyArray<{ code: string; display_name?: string }>): void {
  for (const entry of entries) {
    if (isPrivateTemplateCode(entry.code)) {
      privateTemplateNames.set(entry.code, entry.display_name || entry.code);
    }
  }
}

export function privateTemplateName(code: string): string | null {
  return privateTemplateNames.get(code) ?? null;
}

export function codeForBackendDocType(backendDocType: string | null | undefined): string | null {
  if (!backendDocType) return null;
  if (isPrivateTemplateCode(backendDocType)) return backendDocType;
  // Linear search through all phases + their entries to find a matching
  // backendDocType field. This is fast enough for the small catalog.
  for (const domain of Object.values(DOC_CATALOG) as (DocCatalogPhase[] | unknown)[]) {
    if (Array.isArray(domain)) {
      for (const phase of domain) {
        if ("docs" in phase && Array.isArray(phase.docs)) {
          for (const entry of phase.docs) {
            if (entry.backendDocType === backendDocType) {
              return entry.code;
            }
            // Search children if expandable
            if (entry.expandable && Array.isArray(entry.children)) {
              for (const child of entry.children) {
                if (child.backendDocType === backendDocType) {
                  return child.code;
                }
              }
            }
          }
        }
      }
    }
  }
  return null;
}

/**
 * Translate the backend doc_type codes the server returned from
 * ``GET /doc-types`` (already tier-filtered — a ``pay_per_use`` account gets only the
 * ``doctypes/doc_pricing.json`` codes, paid tiers get all) into the picker's
 * display ``code``s, for use as ``DoctypePicker``'s ``allowedDocCodes``.
 *
 * The server speaks backend codes ("PROTOCOL_DRUG", "CSR"); the picker filters
 * on the display ``code`` ("Protocol", "CSR"). This walks the catalog (including
 * expandable children) and returns each entry's ``code`` whose
 * ``backendDocType ?? code`` the server allows.
 *
 * Pure code-space translation over the existing catalog — no tier logic and no
 * baked-in allowlist. An empty input yields an empty result (fail-closed): the
 * add-in never invents doctypes the API didn't return.
 */
export function uiCodesForBackendDocTypes(backendCodes: Iterable<string>): string[] {
  const allowed = new Set(backendCodes);
  const out: string[] = [];
  const visit = (entry: DocCatalogEntry): void => {
    if (allowed.has(entry.backendDocType ?? entry.code)) out.push(entry.code);
    if (Array.isArray(entry.children)) {
      for (const child of entry.children) visit(child);
    }
  };
  // Same shape-tolerant walk as codeForBackendDocType: DOC_CATALOG's values are
  // the per-domain phase arrays plus non-phase members (sources), so guard on
  // ``phase.docs`` being an array before iterating.
  for (const domain of Object.values(DOC_CATALOG) as unknown[]) {
    if (!Array.isArray(domain)) continue;
    for (const phase of domain as DocCatalogPhase[]) {
      if (Array.isArray(phase.docs)) {
        for (const entry of phase.docs) visit(entry);
      }
    }
  }
  return out;
}

/** Human label for a domain — used as the tab text. */
export const DOMAIN_LABELS: Record<DocDomain, { label: string; hint: string }> = {
  pharma: { label: "Pharma", hint: "FDA · EMA · ICH" },
  device: { label: "Medical Device", hint: "MDR · FDA" },
  shared: { label: "Shared", hint: "IB · Synopsis" },
};

// ---------------------------------------------------------------------------
// Picker categories — the flat, film-style grouping of the catalog
// ---------------------------------------------------------------------------

/**
 * Writer-facing category a doctype is listed under in the doctype picker
 * (small mono uppercase headers: CLINICAL / DEVICES / QUALITY / REGULATORY).
 *
 * # DECISION: derive the category from existing fields (domain, phase,
 * #   framework, code) rather than add a ``category`` field to every entry.
 * # WHY: the catalog stays "one place edit" — a new entry lands in the
 * #      right group with no extra field to remember. Safety reports (DSUR,
 * #      PSUR/PBRER) stay under CLINICAL, matching the launch film.
 * # DATE: 2026-10-14
 */
export type DocCategory = "CLINICAL" | "DEVICES" | "QUALITY" | "REGULATORY";

/** Render order of the picker's category groups. */
export const DOC_CATEGORY_ORDER: readonly DocCategory[] = ["CLINICAL", "DEVICES", "QUALITY", "REGULATORY"];

/** Category for a catalog entry, given the domain + phase it sits in. Pure. */
export function categoryForEntry(entry: DocCatalogEntry, domain: DocDomain, phase: string): DocCategory {
  if (domain === "device") return "DEVICES";
  if (/^CMC\b/i.test(entry.code) || /\bM4Q\b/.test(entry.framework ?? "")) return "QUALITY";
  if (entry.code === "IND" || /meeting|marketing/i.test(phase)) return "REGULATORY";
  return "CLINICAL";
}
